import { useCallback, useEffect, useState } from "react";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CalendarPlus, FileDown, Settings2, ShieldAlert } from "lucide-react";
import { http, downloadBlob } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Btn, Card } from "../kit/Kit";
import { EstadoChip, FlagChip, ST, fmtV, mesL } from "./cov";
import { CellModal, CuraModal, PlanEditor, RegistrarMes } from "./CovForms";

const Chart = ({ data, k, umbral, fmt, label, testid }) => (
  <Card title={label} subtitle={umbral != null ? `Umbral ${fmt(umbral)}` : ""} testid={testid}>
    <div className="h-52 min-w-0"><ResponsiveContainer width="100%" height={208} minWidth={200}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="#F2F0EC" vertical={false} />
        <XAxis dataKey="mes" tickFormatter={mesL} tick={{ fontSize: 11, fill: "#8C867A" }} />
        <YAxis tickFormatter={fmt} tick={{ fontSize: 11, fill: "#8C867A" }} width={56} domain={["auto", "auto"]} />
        <Tooltip formatter={(v) => fmt(v)} labelFormatter={mesL} />
        {umbral != null && <ReferenceLine y={umbral} stroke="#E8643C" strokeDasharray="5 4" label={{ value: "umbral", fill: "#E8643C", fontSize: 11, position: "insideTopRight" }} />}
        <Line type="monotone" dataKey={k} stroke="#111" strokeWidth={2.5} dot={{ r: 4, fill: "#111" }} connectNulls isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer></div>
  </Card>
);

const Matrix = ({ d, onCell }) => {
  const { plan, eval: ev } = d;
  const items = plan.items.filter((i) => i.activo);
  return (
    <div className="overflow-x-auto" data-testid="cov-matrix">
      <table className="w-full border-separate border-spacing-1 text-xs">
        <thead><tr><th className="sticky left-0 bg-white px-2 py-1 text-left font-semibold uppercase tracking-widest text-[#8C867A]">Covenant</th>
          {ev.meses.map((m) => <th key={m} className="px-1 py-1 font-semibold text-[#5C5850]">{mesL(m)}</th>)}
          <th className="px-2 py-1 text-left font-semibold uppercase tracking-widest text-[#8C867A]">Estado</th></tr></thead>
        <tbody>{items.map((it) => (
          <tr key={it.key} data-testid={`cov-matrix-row-${it.key}`}>
            <td className="sticky left-0 min-w-[170px] bg-white px-2 py-1"><p className="font-semibold text-[#111]">{it.nombre}</p><p className="text-[10px] text-[#8C867A]">{it.periodicidad}</p></td>
            {ev.meses.map((m) => {
              const c = ev.celdas[it.key]?.[m];
              return <td key={m} className="p-0">{c ? (
                <button onClick={() => onCell(it, m, c)} data-testid={`cov-cell-${it.key}-${m}`} title={ST[c.s][0]}
                  className={`h-11 w-full min-w-[72px] rounded-lg border px-1 text-[11px] font-semibold transition-transform duration-150 hover:-translate-y-0.5 ${ST[c.s][2]}`}>
                  {ST[c.s][0]}<span className="block font-normal opacity-80">{fmtV(it.tipo, c.v)}</span></button>)
                : <div className="h-11 min-w-[84px] rounded-lg border border-dashed border-[#EDE9E2]" />}</td>;
            })}
            <td className="px-2"><EstadoChip s={ev.ultimo[it.key] || "sin_datos"} testid={`cov-ultimo-${it.key}`} /></td>
          </tr>))}</tbody>
      </table>
    </div>
  );
};

const Flags = ({ id, ev, canCure, onCure }) => !ev.flags.length ? null : (
  <div className="space-y-2" data-testid="cov-flags">{ev.flags.map((f, i) => (
    <div key={i} className="flex flex-wrap items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50/60 px-4 py-3" data-testid={`cov-flag-${i}`}>
      <ShieldAlert size={18} className="text-rose-600" /><FlagChip f={f} />
      <p className="flex-1 text-sm text-[#5C5850]">Desde {mesL(f.desde)} · {f.detalle}</p>
      {f.plan_cura ? <span className="text-xs text-emerald-800" data-testid={`cov-flag-cura-${i}`}>Plan de cura registrado por {f.plan_cura.by}
        {f.plan_cura.archivo && <button className="ml-2 inline-flex items-center gap-1 font-semibold underline" onClick={() => downloadBlob(`/expedientes/${id}/covenants/cures/${f.covenant}/${f.desde}/file`, f.plan_cura.archivo)} data-testid={`cov-flag-cura-file-${i}`}><FileDown size={12} />{f.plan_cura.archivo}</button>}</span>
        : f.tipo !== "vencimiento_anticipado" || f.cura_vence ? (canCure && <Btn kind="secondary" onClick={() => onCure(f)} data-testid={`cov-flag-cura-btn-${i}`}>Registrar plan de cura</Btn>) : null}
    </div>))}</div>
);

export const DealCovenants = ({ id, exp }) => {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [m, setM] = useState(null);
  const load = useCallback(() => http.get(`/expedientes/${id}/covenants`).then((r) => setD(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Card><p className="text-sm text-[#8C867A]">Cargando covenants…</p></Card>;
  if (!d.plan) return <Card testid="cov-sin-plan"><p className="text-sm text-[#8C867A]">El plan de covenants se crea al aprobar el deal.</p></Card>;
  const { plan, eval: ev } = d;
  const it = (k) => plan.items.find((i) => i.key === k && i.activo);
  const close = (changed) => { setM(null); if (changed) load(); };
  const canCure = user.role === "director" || exp.owner_id === user.id;
  return (
    <div className="space-y-6" data-testid="deal-covenants">
      <Card title="Seguimiento de covenants" testid="cov-header"
        subtitle={plan.inicio ? `Periodos mensuales desde ${mesL(plan.inicio)} · último registrado ${mesL(ev.ultimo_periodo)}` : "El seguimiento mensual inicia al registrar la disposición"}
        right={<div className="flex flex-wrap gap-2">
          {d.puede_editar_plan && <Btn kind="secondary" onClick={() => setM({ t: "plan" })} data-testid="cov-editar-plan"><Settings2 size={15} /> Editar plan</Btn>}
          {d.puede_registrar && <Btn kind="accent" onClick={() => setM({ t: "mes" })} data-testid="cov-registrar-mes"><CalendarPlus size={15} /> Registrar mes</Btn>}</div>}>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <EstadoChip s={ev.estado} testid="cov-estado-global" />
          {ev.pendientes.length > 0 && <span className="font-semibold text-[#E8643C]" data-testid="cov-pendientes">Pendientes: {ev.pendientes.map(mesL).join(", ")}</span>}
          {ev.proxima && <span className="text-[#8C867A]">Próxima entrega: {mesL(ev.proxima.mes)} (vence {ev.proxima.vence})</span>}
        </div>
      </Card>
      <Flags id={id} ev={ev} canCure={canCure} onCure={(f) => setM({ t: "cura", f })} />
      {ev.meses.length > 0 && <Card title="Matriz mes × covenant" subtitle="Haz clic en una celda para ver el detalle" testid="cov-matrix-card"><Matrix d={d} onCell={(i, mes, c) => setM({ t: "cell", it: i, mes, c })} /></Card>}
      {ev.meses.length > 0 && <div className="grid gap-6 md:grid-cols-2">
        {it("reciprocidad") && <Chart data={ev.series} k="reciprocidad" umbral={it("reciprocidad").umbral} fmt={(v) => `${(v * 100).toFixed(0)}%`} label="Reciprocidad vs umbral" testid="cov-chart-recip" />}
        {it("dscr") && <Chart data={ev.series.filter((s) => s.dscr != null)} k="dscr" umbral={it("dscr").umbral} fmt={(v) => `${Number(v).toFixed(2)}x`} label="DSCR LTM vs umbral" testid="cov-chart-dscr" />}
      </div>}
      {m?.t === "mes" && <RegistrarMes id={id} d={d} onClose={close} />}
      {m?.t === "plan" && <PlanEditor id={id} plan={plan} onClose={close} />}
      {m?.t === "cell" && <CellModal id={id} {...m} period={d.periods.find((p) => p.mes === m.mes)} dir={d.puede_dispensar} onClose={close} />}
      {m?.t === "cura" && <CuraModal id={id} f={m.f} onClose={close} />}
    </div>
  );
};
